/**
 * User Reference Generator Utility
 * Generates human-readable and unique user references instead of showing long database IDs
 */

export type UserType = 'renter' | 'host' | 'driver' | 'customer' | 'admin' | 'multi-role';

interface UserReferenceConfig {
  prefix: string;
  description: string;
}

// Mapping of user types to their reference prefixes
const USER_TYPE_PREFIXES: Record<UserType, UserReferenceConfig> = {
  'renter': { prefix: 'RNT', description: 'Renter' },
  'host': { prefix: 'HST', description: 'Host' },
  'driver': { prefix: 'DRV', description: 'Driver' },
  'customer': { prefix: 'CUS', description: 'Customer' },
  'admin': { prefix: 'ADM', description: 'Admin' },
  'multi-role': { prefix: 'MRL', description: 'Multi-Role User' }
};

/**
 * Generates a human-readable user reference from the user ID and type
 * @param userId - The original user ID from the database
 * @param userType - The type of user (renter, host, driver, etc.)
 * @returns A formatted user reference (e.g., "RNT-4521", "DRV-8903", "CUS-A3F2")
 */
export function generateUserReference(userId: string, userType: UserType = 'customer'): string {
  const config = USER_TYPE_PREFIXES[userType];

  // If config is undefined, use a default prefix
  if (!config) {
    const identifier = extractIdentifier(userId);
    return `USR-${identifier}`;
  }

  // Extract identifier from the user ID
  const identifier = extractIdentifier(userId);

  return `${config.prefix}-${identifier}`;
}

/**
 * Extracts a meaningful identifier from a user ID
 * Handles various formats like UUIDs, MongoDB ObjectIds, numeric IDs, etc.
 */
function extractIdentifier(userId: string): string {
  if (!userId) return '0000';

  // For UUIDs or long IDs, take last 4 characters
  if (userId.length > 8) {
    return userId.slice(-4).toUpperCase();
  }

  // For numeric IDs
  const numbers = userId.match(/\d+/g);
  if (numbers && numbers.length > 0) {
    const allNumbers = numbers.join('');
    return allNumbers.length > 4 ? allNumbers.slice(-4) : allNumbers.padStart(4, '0');
  }

  // For short alphanumeric IDs
  return userId.toUpperCase().padStart(4, '0');
}

/**
 * Get the description for a user type prefix
 */
export function getUserTypeDescription(userType: UserType): string {
  return USER_TYPE_PREFIXES[userType]?.description || 'Unknown';
}

/**
 * Parse a user reference back to its components
 * @param reference - The user reference (e.g., "RNT-4521")
 * @returns Object with prefix and identifier, or null if invalid format
 */
export function parseUserReference(reference: string): { prefix: string; identifier: string; type?: UserType } | null {
  const match = reference.match(/^([A-Z]+)-(.+)$/);
  if (!match) return null;

  const [, prefix, identifier] = match;

  // Find the user type that matches this prefix
  const type = Object.entries(USER_TYPE_PREFIXES).find(([, config]) => config.prefix === prefix)?.[0] as UserType;

  return { prefix, identifier, type };
}

/**
 * Validate if a user reference follows the correct format
 */
export function isValidUserReference(reference: string): boolean {
  return parseUserReference(reference) !== null;
}

/**
 * Generate a short customer reference (legacy support)
 * @param customerId - The customer ID
 * @returns A formatted customer reference (e.g., "CUS-A3F2")
 */
export function generateCustomerReference(customerId?: string): string {
  if (!customerId) return 'N/A';
  return generateUserReference(customerId, 'customer');
}
